import type { HttpCore } from '../http';
import type { CountryCode, TrackRole, CertCompletion, ContentLanguage } from './me';

/**
 * Types mirror `frontend/web-ide/src/services/portal.ts` field-for-field —
 * that file is the extraction source, not a redesign.
 */

export type LessonStatus = 'done' | 'current' | 'unstarted';
export type LessonType = 'lecture' | 'reading';

export interface CourseDetailCourse {
  slug: string;
  title: string;
  order: number;
  cert_slug: string | null;
  current_lesson_slug: string | null;
  module_label?: string;
}

export interface CourseDetailLesson {
  slug: string;
  title: string;
  order: number;
  status: LessonStatus;
  duration_sec: number;
  type?: LessonType;
  reading?: { course_ref: string; chapter_ref?: string };
  video_url: string | null;
  has_lab?: boolean;
  last_position_sec?: number;
}

export interface CourseDetailCurrentLesson {
  slug: string;
  title: string;
  video_url: string | null;
  duration_sec: number;
  watched_pct: number;
  last_position_sec?: number;
}

export interface CourseDetailLab {
  slug: string;
  title: string;
  status: string;
  lab_id: string | null;
  delivery?: Record<string, unknown> | null;
}

export interface CourseDetailQuiz {
  id: string | null;
  slug: string;
  title: string;
  question_count: number;
  status: 'available' | 'locked' | 'passed' | 'preview' | 'preparing';
  submittable?: boolean;
  position?: number;
  module?: string | null;
}

export interface CourseDetailResource {
  title: string;
  url: string;
}

export type CourseDetailItemType = 'lecture' | 'reading' | 'lab' | 'assessment';
export type CourseDetailItemState = 'not_started' | 'in_progress' | 'completed';
export type CourseDetailItemAvailability = 'available' | 'locked' | 'preparing';

export interface CourseDetailItem {
  id: string;
  type: CourseDetailItemType;
  module: string | null;
  position: number;
  title: string;
  duration_sec: number | null;
  state: CourseDetailItemState;
  availability: CourseDetailItemAvailability;
  counts_toward_completion: boolean;
  progress_pct: number | null;
  href: string | null;
  related_lab_item_ids: string[];
  primary_lab_item_id: string | null;
  lecture?: {
    lesson_slug: string;
    video_external_id: string | null;
    video_url: string | null;
    last_position_sec: number;
  };
  reading?: { course_book_slug: string; chapter_id: string };
  lab?: { lab_id: string | null; lab_slug: string | null; delivery?: Record<string, unknown> | null };
  assessment?: {
    question_count: number;
    attempt_count: number;
    best_score_pct: number | null;
    passed: boolean;
  };
}

export interface CourseDetailProgress {
  total_items: number;
  completable_items: number;
  completed_items: number;
  percentage: number;
  state: 'not_started' | 'in_progress' | 'completed';
  current_item_id: string | null;
  next_item_id: string | null;
}

export interface CourseDetailPayload {
  course: CourseDetailCourse;
  current_lesson: CourseDetailCurrentLesson | null;
  lessons: CourseDetailLesson[];
  items?: CourseDetailItem[];
  progress?: CourseDetailProgress;
  tabs: {
    labs: CourseDetailLab[];
    quizzes: CourseDetailQuiz[];
    discussion_count: number;
    resources: CourseDetailResource[];
  };
  textbook_chapter_md: string;
  has_lab: boolean;
  preview: boolean;
}

export type CertCourseStatus = 'not_started' | 'in_progress' | 'completed' | 'locked';

export interface CertOverviewCourse {
  slug: string;
  title: string;
  order: number;
  progress_pct: number;
  status: CertCourseStatus;
  prereq_slugs: string[];
  is_optional: boolean;
}

export interface CertOverviewCert {
  slug: string;
  title: string;
  short_description: string;
  progress_pct: number;
  founding_member_number: number | null;
  track_role: TrackRole;
  is_unlocked: boolean;
  is_complete: boolean;
  completion: CertCompletion | null;
}

export interface CertPricing {
  price: number;
  founding_discount_pct: number;
  currency: string;
  tax_rate: number | null;
  tax_label: string | null;
}

export interface CertOverviewPayload {
  cert: CertOverviewCert;
  courses: CertOverviewCourse[];
  capstone: { title: string; unlocked: boolean } | null;
  country: CountryCode;
  pricing: CertPricing | null;
}

/** GET /portal/certs — no server route answers a bare list today (only
 *  `/portal/certs/:slug` exists); the ladder is derived client-side from
 *  `me.getVerticalsDetail()`. Typed per the inventory contract regardless,
 *  in case a real list endpoint lands later. */
export interface CertListEntry {
  slug: string;
  title: string;
  short_description: string;
  progress_pct: number;
}

export interface GradesStat {
  value: string;
  label: string;
}

export type GradesEvidenceConfidence = 'unavailable' | 'provisional' | 'confident';

export interface GradesEvidenceCount {
  status: GradesEvidenceConfidence;
  evidence_count: number;
  confident_at: number;
}

export interface GradesRubricRow {
  component: string;
  weight_pct: number;
  your_score: string;
  class_median: string;
  contribution: number;
  your_evidence: GradesEvidenceCount;
  class_evidence: GradesEvidenceCount;
}

export interface GradesRubric {
  cert_slug: string | null;
  cert_title: string;
  rows: GradesRubricRow[];
}

export interface GradesFeedbackEntry {
  title: string;
  score: string;
  context: string;
  quote: string;
  attribution: string;
  graded_on: string;
}

export interface GradesLabSubmission {
  submission_id: string;
  lab_id: string;
  title: string;
  cert_slug: string | null;
  cert_title: string | null;
  status: string;
  score_pct: number | null;
  score_label: string;
  submitted_at: string | null;
  graded_at: string | null;
  instructor_feedback: string | null;
  tests: { status: 'available' | 'unavailable'; passed: number | null; failed: number | null; total: number | null };
}

export interface GradesPayload {
  scope?: { kind: 'course'; course_id: string; course_slug: string; course_title: string };
  quiz_attempts?: Array<{ attempt_id: string; assessment_id: string; title: string; score_pct: number | null; passed: boolean; submitted_at: string | null }>;
  gpa: number;
  gpa_context: string;
  stats: GradesStat[];
  confidence: { gpa: GradesEvidenceCount; rubric: Record<GradesEvidenceConfidence, number> };
  certs_available: Array<{ slug: string; title: string }>;
  rubric: GradesRubric;
  feedback: GradesFeedbackEntry[];
  lab_submissions: GradesLabSubmission[];
}

export interface PortfolioCheckpoint {
  id: string;
  commit_sha: string;
  tree_sha: string;
  run_attempt_id: string;
  run_status: 'passed';
  reflection: string;
  decision_record: Record<string, unknown>;
  git_verified_at: string;
  created_at: string;
  run_completed_at: string;
  workspace_evidence: { aggregate_hash: string | null; snapshot_count: number };
}

export interface PortfolioProject {
  id: string;
  lab: { id: string; title: string };
  course_item: { id: string; title: string };
  course: { slug: string; title: string };
  artifact_release_id: string;
  visibility: 'private';
  starter: { commit_sha: string; tree_sha: string; authorship: 'hauraki_system' };
  created_at: string;
  updated_at: string;
  checkpoints: PortfolioCheckpoint[];
}

export interface PortfolioPayload {
  status: 'available' | 'empty';
  visibility: 'private';
  capabilities: { github_export: false; public_sharing: false };
  projects: PortfolioProject[];
}

export interface AgendaItem {
  lab_title: string;
  course_slug: string;
  course_title: string;
  cert_name: string | null;
  due_date: string;
  status: 'completed' | 'overdue' | 'upcoming';
  href: string;
}

export interface AgendaPayload {
  has_cohort: boolean;
  items: AgendaItem[];
}

export interface LessonMediaChapter {
  slug: string;
  title: string;
  offset_sec: number;
}

export interface LessonMediaTranscriptRow {
  offset_sec: number;
  text: string;
}

export interface LessonMedia {
  lesson_slug: string;
  video_url: string | null;
  duration_sec: number | null;
  /** Duration of the canonical lecture used to persist shared lesson progress. */
  canonical_duration_sec?: number | null;
  chapters: LessonMediaChapter[];
  transcript: LessonMediaTranscriptRow[];
  requested_language?: ContentLanguage;
  resolved_language?: ContentLanguage;
  language_fallback?: boolean;
  available_languages?: ContentLanguage[];
}

export interface TutorHistoryMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
}

export interface CertCredential {
  available: boolean;
  status: 'available' | 'pending' | 'revoked';
  credential_id: string | null;
  cert_title: string;
  issued_at: string | null;
  share_url: string | null;
  download_url: string | null;
}

export interface CohortStats {
  has_cohort: boolean;
  cohort_size: number;
  cohort_avg_progress_pct: number;
  your_progress_pct: number;
}

export function createPortalModule(core: HttpCore) {
  return {
    listCerts: () => core.request<{ certs: CertListEntry[] }>('/portal/certs').then((r) => r.certs ?? []),
    getCertOverview: (certSlug: string) =>
      core.request<CertOverviewPayload>(`/portal/certs/${encodeURIComponent(certSlug)}`),
    getCertCredential: (certSlug: string) =>
      core.request<CertCredential>(`/portal/certs/${encodeURIComponent(certSlug)}/credential`),
    getCourse: (courseSlug: string) =>
      core.request<CourseDetailPayload>(`/portal/courses/${encodeURIComponent(courseSlug)}`),
    getLessonMedia: (courseSlug: string, lessonSlug: string, language?: ContentLanguage | null) =>
      core.request<LessonMedia>(
        `/portal/courses/${encodeURIComponent(courseSlug)}/lessons/${encodeURIComponent(lessonSlug)}/media`,
        language ? { params: { language } } : undefined,
      ),
    /**
     * Fire-and-forget progress tick. Idempotent — routed through
     * `core.postIdempotent` so an offline learner queues the update (client
     * merges to the GREATEST `watched_pct` on collision) instead of losing it,
     * mirroring the server's own `GREATEST()` upsert.
     */
    postLessonProgress: (
      courseSlug: string,
      lessonSlug: string,
      watchedPct: number,
      lastPositionSec?: number,
      /** Bounded engagement deltas since the last report (server clamps + rate-limits). */
      signals?: { replays?: number; seeks?: number },
    ) =>
      core.postIdempotent(
        `/portal/courses/${encodeURIComponent(courseSlug)}/lessons/${encodeURIComponent(lessonSlug)}/progress`,
        {
          watched_pct: watchedPct,
          ...(lastPositionSec == null ? {} : { last_position_sec: lastPositionSec }),
          ...(signals?.replays ? { replays: signals.replays } : {}),
          ...(signals?.seeks ? { seeks: signals.seeks } : {}),
        },
        {
          key: `lesson-progress:${courseSlug}:${lessonSlug}`,
          mergeNumericField: 'watched_pct',
          sumNumericFields: ['replays', 'seeks'],
          // Mirror the server's per-request clamp so merged offline deltas are not silently truncated.
          sumNumericCaps: { replays: 3, seeks: 30 },
        },
      ),
    getGrades: (certSlug?: string, courseSlug?: string) =>
      core.request<GradesPayload>(
        '/portal/grades',
        certSlug || courseSlug ? { params: { ...(certSlug ? { cert: certSlug } : {}), ...(courseSlug ? { course: courseSlug } : {}) } } : undefined,
      ),
    getPortfolio: () => core.request<PortfolioPayload>('/portal/portfolio'),
    getCohortStats: () => core.request<CohortStats>('/portal/cohort/stats'),
    getAgenda: () => core.request<AgendaPayload>('/portal/agenda'),
    getTutorHistory: () =>
      core.request<{ messages: TutorHistoryMessage[] }>('/portal/tutor/history').then((r) => r.messages),
    saveTutorMessages: (messages: Array<{ id?: string; role: 'user' | 'assistant'; content: string; context?: unknown; sources?: unknown }>) =>
      core.request<{ saved: number }>('/portal/tutor/history', { method: 'POST', body: { messages } }).then(() => undefined),
  };
}

export type PortalModule = ReturnType<typeof createPortalModule>;
